import EquipmentCategory from '../../data-provider/models/equipment/EquipmentCategory';
import UserEquipmentSettings from '../../data-provider/models/user/UserEquipmentSettings';
import { htmlToElement } from '../../helper/html.helper';
import EquipmentMin from '../../data-provider/models/equipment/EquipmentMin';
import Decoration from '../../data-provider/models/equipment/Decoration';
import EquipmentSkills from '../../data-provider/models/equipment/EquipmentSkills';

const WIKI_BASE_URL = 'https://mhwiki.axibug.com/mh2/bugu/';

const getPartPath = (category: EquipmentCategory): string => {
  switch (category) {
    case EquipmentCategory.HEAD: return 'head';
    case EquipmentCategory.CHEST: return 'armor';
    case EquipmentCategory.ARMS: return 'arm';
    case EquipmentCategory.WAIST: return 'waist';
    case EquipmentCategory.LEGS: return 'foot';
    default: return '';
  }
};

const getWikiUrl = (name: string, id: string | undefined, part: string): string | null => {
  if (!id || !part) return null;
  return `${WIKI_BASE_URL}${part}.html#${id}`;
};

const STORAGE_KEY = 'dos-eq-settings';

const saveToStorage = () => {
  window.localStorage.setItem(STORAGE_KEY, UserEquipmentSettings.Instance.serialize());
};

const HR_LIMIT_MAP: Record<number, string> = {
  1: '离线模式',
  2: '在线 HR0/1+',
  3: '在线 HR10+',
  4: '在线 HR17+',
  5: '在线 HR20+',
  6: '在线 HR31+',
  7: '在线 HR38+',
  8: '在线 HR48+',
  9: '在线 HR51+',
};
const getFromStorage = () => window.localStorage.getItem(STORAGE_KEY);

let skillNameMap: Map<number, string> | null = null;

const getSkillsDisplayHTML = (skills: EquipmentSkills): string => {
  if (!skillNameMap || skills.size === 0) return '';
  const lines: string[] = [];
  for (const [sId, val] of skills) {
    const name = skillNameMap.get(sId) || `技能${sId}`;
    lines.push(`${name}：${val}`);
  }
  return lines.join('<br>');
};

const createExclusionElement = (name: string, onRemove: () => void) => {
  const root = document.createElement('div');
  root.style.textAlign = 'left';
  root.setAttribute('data-name', name);
  root.classList.add('eq-exclusion-ele');

  const remove = document.createElement('span');
  remove.textContent = 'X';
  remove.style.marginRight = '0.6em';
  remove.style.marginLeft = '0.1em';
  remove.style.cursor = 'pointer';
  remove.addEventListener('click', () => {
    onRemove();
    saveToStorage();
  });

  const content = document.createElement('span');
  content.textContent = name;

  root.appendChild(remove);
  root.appendChild(content);
  return root;
};

const getExclusionElement = (x: EquipmentMin) => {
  return createExclusionElement(x.name, () => removeExlusion(x));
};

const getPinPicker = (cat: EquipmentCategory, eq: EquipmentMin[]) => {
  const root = document.createElement('div');
  root.style.display = 'flex';
  root.style.flexDirection = 'column';
  root.style.alignItems = 'center';
  root.style.gap = '0.3em';
  root.style.textAlign = 'center';

  const skillDisplay = document.createElement('div');
  skillDisplay.id = `eq-${cat}-skill-display`;
  skillDisplay.style.width = '100%';
  skillDisplay.style.textAlign = 'left';
  skillDisplay.style.fontSize = '0.85em';
  skillDisplay.style.color = '#555';
  skillDisplay.style.minHeight = '1.2em';
  skillDisplay.style.padding = '0.2em 0';
  skillDisplay.style.borderBottom = '1px dashed #ccc';
  root.appendChild(skillDisplay);

  const slotsContainer = document.createElement('div');
  slotsContainer.style.display = 'flex';
  slotsContainer.style.alignItems = 'center';
  slotsContainer.style.justifyContent = 'space-between';
  slotsContainer.style.width = '100%';
  slotsContainer.style.padding = '0.2em 0';
  slotsContainer.style.borderBottom = '1px dashed #ccc';

  const slotsDisplay = document.createElement('span');
  slotsDisplay.id = `eq-${cat}-slots-display`;
  slotsDisplay.style.fontSize = '0.85em';
  slotsDisplay.style.color = '#222';
  slotsDisplay.style.minHeight = '1.2em';
  slotsDisplay.textContent = '孔位数：';

  const detailBtn = document.createElement('span');
  detailBtn.textContent = '详情';
  detailBtn.style.cursor = 'pointer';
  detailBtn.style.border = '1px solid var(--color-border)';
  detailBtn.style.borderRadius = '3px';
  detailBtn.style.padding = '0 0.6em';
  detailBtn.style.fontSize = '0.9em';
  detailBtn.style.backgroundColor = 'var(--color-background)';
  detailBtn.addEventListener('click', () => {
    const selectedName = content.value;
    if (selectedName === '无') return;
    const selectedEq = eq.find(e => e.name === selectedName);
    if (selectedEq && selectedEq.id) {
      const part = getPartPath(cat);
      const url = getWikiUrl(selectedName, selectedEq.id, part);
      if (url) window.open(url, 'equip');
    }
  });

  slotsContainer.appendChild(slotsDisplay);
  slotsContainer.appendChild(detailBtn);
  root.appendChild(slotsContainer);

  const hrlimitDisplay = document.createElement('div');
  hrlimitDisplay.id = `eq-${cat}-hrlimit-display`;
  hrlimitDisplay.style.width = '100%';
  hrlimitDisplay.style.textAlign = 'left';
  hrlimitDisplay.style.fontSize = '0.85em';
  hrlimitDisplay.style.color = '#222';
  hrlimitDisplay.style.minHeight = '1.2em';
  hrlimitDisplay.style.padding = '0.2em 0';
  hrlimitDisplay.style.borderBottom = '1px dashed #ccc';
  root.appendChild(hrlimitDisplay);

  const selectContainer = document.createElement('div');
  selectContainer.style.display = 'flex';
  selectContainer.style.alignItems = 'center';
  selectContainer.style.gap = '0.5em';
  selectContainer.style.width = '100%';
  selectContainer.style.justifyContent = 'center';

  const content = document.createElement('select');
  content.setAttribute('id', `eq-${cat}-pin-picker`);
  content.style.width = '80%';
  content.style.textAlign = 'center';

  const sortedEq = [...eq].sort((a, b) =>
    a.name.localeCompare(b.name, 'zh-Hans-CN', { sensitivity: 'accent' })
  );
  const options = [{ name: '无', category: cat }, ...sortedEq];

  for (const x of options) {
    content.appendChild(htmlToElement(`<option value="${x.name}">${x.name}</option>`));
  }

  selectContainer.appendChild(content);
  root.appendChild(selectContainer);

  const updateSkillDisplay = (selectedName: string) => {
    const selectedEq = eq.find(e => e.name === selectedName);
    if (selectedEq && 'skills' in selectedEq) {
      const skills = (selectedEq as any).skills as EquipmentSkills;
      skillDisplay.innerHTML = getSkillsDisplayHTML(skills);
      const slots = (selectedEq as any).slots;
      slotsDisplay.textContent = `孔位数：${slots}`;
      const hrlimit = (selectedEq as any).hrlimit;
      const hrText = HR_LIMIT_MAP[hrlimit] || `HR${hrlimit}`;
      hrlimitDisplay.innerHTML = `最低生产等级：${hrText}`;
      slotsContainer.style.display = 'flex';
    } else {
      skillDisplay.innerHTML = '';
      slotsDisplay.textContent = '孔位数：';
      hrlimitDisplay.innerHTML = '';
      slotsContainer.style.display = 'none';
    }
  };

  content.addEventListener('change', () => {
    const selectedName = content.value;
    updateSkillDisplay(selectedName);
    addPin({ name: selectedName, category: cat });
  });

  const buttonContainer = document.createElement('div');
  buttonContainer.style.display = 'flex';
  buttonContainer.style.gap = '1.0em';
  buttonContainer.style.justifyContent = 'center';

  const remove = htmlToElement('<span>取消固定</span>') as HTMLSpanElement;
  remove.style.cursor = 'pointer';
  remove.style.border = '1px solid var(--color-border)';
  remove.style.borderRadius = '3px';
  remove.style.padding = '0 0.6em';
  remove.style.fontSize = '0.9em';
  remove.addEventListener('click', () => removePin(cat));

  const excludeBtn = htmlToElement('<span>添加到排除</span>') as HTMLSpanElement;
  excludeBtn.style.cursor = 'pointer';
  excludeBtn.style.color = 'var(--color-negative-skill)';
  excludeBtn.style.border = '1px solid var(--color-negative-skill)';
  excludeBtn.style.borderRadius = '3px';
  excludeBtn.style.padding = '0 0.6em';
  excludeBtn.style.fontSize = '0.9em';
  excludeBtn.style.backgroundColor = 'rgba(206, 8, 8, 0.05)';
  excludeBtn.addEventListener('click', () => {
    const selectedName = content.value;
    if (selectedName === '无') return;
    const selectedEq = eq.find(e => e.name === selectedName);
    if (!selectedEq) return;
    addExclusion(selectedEq);
    content.value = '无';
    removePin(cat);
  });

  buttonContainer.appendChild(remove);
  buttonContainer.appendChild(excludeBtn);

  root.appendChild(buttonContainer);

  const currentPin = UserEquipmentSettings.Instance.pins[cat];
  if (currentPin) {
    content.value = currentPin.name;
    updateSkillDisplay(currentPin.name);
  } else {
    slotsContainer.style.display = 'none';
  }
  return root;
};

const getDecorationPicker = (decorations: Decoration[]) => {
  const root = document.createElement('div');
  root.className = 'eq-column';
  root.setAttribute('data-eq-column-type', 'decoration');

  const pickHeader = htmlToElement('<div class="eq-column-item eq-column-header">装饰品 排除</div>') as HTMLElement;
  const pickContent = document.createElement('div');
  pickContent.className = 'eq-column-item eq-column-content eq-column-pin';
  pickContent.style.display = 'flex';
  pickContent.style.flexDirection = 'column';
  pickContent.style.alignItems = 'center';
  pickContent.style.gap = '0.3em';

  const skillDisplay = document.createElement('div');
  skillDisplay.id = 'decoration-skill-display';
  skillDisplay.style.width = '100%';
  skillDisplay.style.textAlign = 'left';
  skillDisplay.style.fontSize = '0.85em';
  skillDisplay.style.color = '#555';
  skillDisplay.style.minHeight = '1.2em';
  skillDisplay.style.padding = '0.2em 0';
  skillDisplay.style.borderBottom = '1px dashed #ccc';
  pickContent.appendChild(skillDisplay);

  const slotsContainer = document.createElement('div');
  slotsContainer.style.display = 'flex';
  slotsContainer.style.alignItems = 'center';
  slotsContainer.style.justifyContent = 'space-between';
  slotsContainer.style.width = '100%';
  slotsContainer.style.padding = '0.2em 0';
  slotsContainer.style.borderBottom = '1px dashed #ccc';

  const slotsDisplay = document.createElement('span');
  slotsDisplay.id = 'decoration-slots-display';
  slotsDisplay.style.fontSize = '0.85em';
  slotsDisplay.style.color = '#222';
  slotsDisplay.style.minHeight = '1.2em';
  slotsDisplay.textContent = '所需孔位：';

  const detailBtn = document.createElement('span');
  detailBtn.textContent = '详情';
  detailBtn.style.cursor = 'pointer';
  detailBtn.style.border = '1px solid var(--color-border)';
  detailBtn.style.borderRadius = '3px';
  detailBtn.style.padding = '0 0.6em';
  detailBtn.style.fontSize = '0.9em';
  detailBtn.style.backgroundColor = 'var(--color-background)';
  detailBtn.addEventListener('click', () => {
    const selectedName = select.value;
    if (!selectedName) return;
    const selectedDeco = decorations.find(d => d.name === selectedName);
    if (selectedDeco && selectedDeco.id) {
      const url = getWikiUrl(selectedName, selectedDeco.id, 'soushoku');
      if (url) window.open(url, 'equip');
    }
  });

  slotsContainer.appendChild(slotsDisplay);
  slotsContainer.appendChild(detailBtn);
  pickContent.appendChild(slotsContainer);

  const hrlimitDisplay = document.createElement('div');
  hrlimitDisplay.id = 'decoration-hrlimit-display';
  hrlimitDisplay.style.width = '100%';
  hrlimitDisplay.style.textAlign = 'left';
  hrlimitDisplay.style.fontSize = '0.85em';
  hrlimitDisplay.style.color = '#222';
  hrlimitDisplay.style.minHeight = '1.2em';
  hrlimitDisplay.style.padding = '0.2em 0';
  hrlimitDisplay.style.borderBottom = '1px dashed #ccc';
  pickContent.appendChild(hrlimitDisplay);

  const selectContainer = document.createElement('div');
  selectContainer.style.display = 'flex';
  selectContainer.style.alignItems = 'center';
  selectContainer.style.gap = '0.5em';
  selectContainer.style.width = '100%';
  selectContainer.style.justifyContent = 'center';

  const select = document.createElement('select');
  select.style.width = '80%';
  select.style.textAlign = 'center';
  const noneOption = document.createElement('option');
  noneOption.value = '';
  noneOption.textContent = '无';
  select.appendChild(noneOption);
  const sorted = [...decorations].sort((a, b) => a.name.localeCompare(b.name, 'zh-Hans-CN', { sensitivity: 'accent' }));
  for (const deco of sorted) {
    const option = document.createElement('option');
    option.value = deco.name;
    option.textContent = deco.name;
    select.appendChild(option);
  }

  selectContainer.appendChild(select);
  pickContent.appendChild(selectContainer);

  const updateDecoSkillDisplay = (selectedName: string) => {
    const selectedDeco = decorations.find(d => d.name === selectedName);
    if (selectedDeco && 'skills' in selectedDeco) {
      const skills = (selectedDeco as any).skills as EquipmentSkills;
      skillDisplay.innerHTML = getSkillsDisplayHTML(skills);
      const requiredSlots = (selectedDeco as any).requiredSlots;
      slotsDisplay.textContent = `所需孔位：${requiredSlots}`;
      const hrlimit = (selectedDeco as any).hrlimit;
      const hrText = HR_LIMIT_MAP[hrlimit] || `HR${hrlimit}`;
      hrlimitDisplay.innerHTML = `最低生产等级：${hrText}`;
      slotsContainer.style.display = 'flex';
    } else {
      skillDisplay.innerHTML = '';
      slotsDisplay.textContent = '所需孔位：';
      hrlimitDisplay.innerHTML = '';
      slotsContainer.style.display = 'none';
    }
  };

  select.addEventListener('change', () => {
    updateDecoSkillDisplay(select.value);
  });

  const buttonContainer = document.createElement('div');
  buttonContainer.style.display = 'flex';
  buttonContainer.style.gap = '1.0em';
  buttonContainer.style.justifyContent = 'center';
  const excludeBtn = document.createElement('span');
  excludeBtn.textContent = '添加到排除';
  excludeBtn.style.cursor = 'pointer';
  excludeBtn.style.color = 'var(--color-negative-skill)';
  excludeBtn.style.border = '1px solid var(--color-negative-skill)';
  excludeBtn.style.borderRadius = '3px';
  excludeBtn.style.padding = '0 0.6em';
  excludeBtn.style.fontSize = '0.9em';
  excludeBtn.style.backgroundColor = 'rgba(206, 8, 8, 0.05)';
  excludeBtn.addEventListener('click', () => {
    const name = select.value;
    if (!name) return;
    UserEquipmentSettings.Instance.addDecorationExclusion(name);
    saveToStorage();
    refreshDecorationExclusionList();
    select.value = '';
    updateDecoSkillDisplay('');
  });
  buttonContainer.appendChild(excludeBtn);

  pickContent.appendChild(buttonContainer);

  const listHeader = htmlToElement('<div class="eq-column-item eq-column-header">排除的装饰品</div>') as HTMLElement;
  const listContent = document.createElement('div');
  listContent.className = 'eq-column-item eq-column-content eq-column-exclusion';
  listContent.id = 'eq-decoration-exclusion';
  listContent.style.display = 'flex';
  listContent.style.flexDirection = 'column';
  listContent.style.alignItems = 'flex-start';

  root.appendChild(pickHeader);
  root.appendChild(pickContent);
  root.appendChild(listHeader);
  root.appendChild(listContent);

  refreshDecorationExclusionList();

  slotsContainer.style.display = 'none';

  return root;
};

const renderColumns = (armor: EquipmentMin[][], decorations: Decoration[], skillNameMapParam: Map<number, string>) => {
  skillNameMap = skillNameMapParam;
  const parent = document.getElementById('eq-container');
  if (!parent) return;
  parent.innerHTML = '';

  const categories = [
    [EquipmentCategory.HEAD, '头', armor[0]],
    [EquipmentCategory.CHEST, '胴', armor[1]],
    [EquipmentCategory.ARMS, '腕', armor[2]],
    [EquipmentCategory.WAIST, '腰', armor[3]],
    [EquipmentCategory.LEGS, '脚', armor[4]],
  ];
  for (const [cat, name, eq] of categories) {
    const root = htmlToElement(`<div class="eq-column" data-eq-column-type="${cat}"></div>`);

    const pinHeader = htmlToElement(`<div class="eq-column-item eq-column-header">${name} 固定选择</div>`);
    const pinContent = htmlToElement('<div class="eq-column-item eq-column-content eq-column-pin"></div>');
    pinContent.appendChild(getPinPicker(cat as EquipmentCategory, eq as EquipmentMin[]));

    const exclusionHeader = htmlToElement(`<div class="eq-column-item eq-column-header">排除的${name}装备</div>`);
    const exclusionContent = htmlToElement(`<div id="eq-${cat}-exclusion" class="eq-column-item eq-column-content eq-column-exclusion"></div>`);

    root.appendChild(pinHeader);
    root.appendChild(pinContent);
    root.appendChild(exclusionHeader);
    root.appendChild(exclusionContent);
    parent.appendChild(root);
  }

  parent.appendChild(getDecorationPicker(decorations));
};

const _addExclusion = (x: EquipmentMin) => {
  const parent = document.getElementById(`eq-${x.category}-exclusion`);
  if (parent) parent.appendChild(getExclusionElement(x));
};

export const refreshDecorationExclusionList = () => {
  const listContainer = document.getElementById('eq-decoration-exclusion');
  if (!listContainer) return;
  listContainer.innerHTML = '';
  const excludedNames = UserEquipmentSettings.Instance.decorationExclusions;
  if (excludedNames.length === 0) return;
  for (const name of excludedNames) {
    const element = createExclusionElement(name, () => {
      UserEquipmentSettings.Instance.removeDecorationExclusion(name);
      refreshDecorationExclusionList();
    });
    listContainer.appendChild(element);
  }
};

export const removeExlusion = (x: EquipmentMin) => {
  const ele = Array.from(document.getElementsByClassName('eq-exclusion-ele')).find(
    (a) => (a as HTMLElement).getAttribute('data-name') === x.name
  ) as HTMLElement;
  if (!ele) return;
  ele.remove();
  UserEquipmentSettings.Instance.removeExclusion(x);
  saveToStorage();
};

export const removePin = (cat: EquipmentCategory) => {
  const ele = document.getElementById(`eq-${cat}-pin-picker`) as HTMLSelectElement;
  if (ele) ele.selectedIndex = 0;
  UserEquipmentSettings.Instance.removePin(cat);
  saveToStorage();
  const skillDisplay = document.getElementById(`eq-${cat}-skill-display`);
  if (skillDisplay) skillDisplay.innerHTML = '';
  const slotsDisplay = document.getElementById(`eq-${cat}-slots-display`);
  const slotsContainer = slotsDisplay ? slotsDisplay.parentElement : null;
  if (slotsContainer) slotsContainer.style.display = 'none';
  const hrlimitDisplay = document.getElementById(`eq-${cat}-hrlimit-display`);
  if (hrlimitDisplay) hrlimitDisplay.innerHTML = '';
};

export const addExclusion = (x: EquipmentMin) => {
  if (UserEquipmentSettings.Instance.hasExclusion(x)) return;
  UserEquipmentSettings.Instance.addExclusion(x);
  _addExclusion(x);
  saveToStorage();
};

export const addPin = (x: EquipmentMin) => {
  if (x.name === '无') {
    UserEquipmentSettings.Instance.removePin(x.category);
    saveToStorage();
    const display = document.getElementById(`eq-${x.category}-skill-display`);
    if (display) display.innerHTML = '';
    return;
  }
  UserEquipmentSettings.Instance.addPin(x);
  saveToStorage();
  const select = document.getElementById(`eq-${x.category}-pin-picker`) as HTMLSelectElement;
  if (select) {
    select.value = x.name;
    select.dispatchEvent(new Event('change'));
  }
};

export const renderEqSettings = (armor: EquipmentMin[][], decorations: Decoration[], skillNameMapParam: Map<number, string>) => {
  renderColumns(armor, decorations, skillNameMapParam);
  const raw = getFromStorage();
  if (raw) UserEquipmentSettings.Instance.deserialize(raw);

  for (const exclusionList of UserEquipmentSettings.Instance.exclusions) {
    for (const x of exclusionList) {
      _addExclusion(x);
    }
  }

  UserEquipmentSettings.Instance.pins.forEach((x, i) => {
    if (x) addPin(x);
    else removePin(i);
  });
  refreshDecorationExclusionList();
};